import React, { useState, useRef, useEffect } from 'react';
import { C } from '../tokens';
import Icon from '../components/Icon';
import { Card, Pill, Btn } from '../components/UI';
import useBreakpoint from '../useBreakpoint';

/* ═══════════════════════════════════════════════════════════
   Rich conversation data — each contact owns profile,
   messages, stages, emails, details, and filter status
   ═══════════════════════════════════════════════════════════ */
const CONVERSATIONS = [
  {
    id: 1,
    name: 'Budi Santoso', initials: 'BS',
    avatarGrad: `linear-gradient(135deg, ${C.teal}, ${C.indigo})`,
    role: 'Head of Operations at TechIndo',
    email: 'budi.s@example.com', phone: '+62 812-3456-7890', location: 'Jakarta, Indonesia',
    online: true,
    pills: [['B2B', '#f1eeec', C.t2], ['VIP Client', '#f1eeec', C.t2], ['Qualified', C.pL, C.primary]],
    time: '12:45 PM',
    preview: "Can we schedule a proposal review? I'm interested in...",
    tags: [['Proposal', '#3b82f6'], ['Priority', '#f59e0b']],
    status: 'active',
    stages: [
      { label: 'New Inquiry', sub: 'Completed Sep 12', state: 'check' },
      { label: 'Discovery Call', sub: 'Completed Sep 14', state: 'check' },
      { label: 'Proposal Sent', sub: 'In Progress', state: 'current' },
      { label: 'Closing', sub: 'Pending', state: 'pending' },
    ],
    details: {
      company: 'TechIndo Solutions', industry: 'SaaS / Logistics', dealValue: '$12,500',
      source: 'LinkedIn', assigned: 'Sarah Chen', priority: 'High', created: 'Sep 10, 2025'
    },
    dateLabel: 'TODAY, SEP 15',
    messages: [
      { bot: true, time: '09:15 AM', text: "Hello Budi! I see you're interested in our Enterprise Q4 expansion plan. I can certainly help you with the proposal details. Would you like to see the breakdown of the features first?" },
      { bot: false, time: '09:42 AM', text: 'Yes, please. Especially the AI-driven analytics part. We need to know if it integrates with our current CRM.' },
      { bot: true, time: '09:43 AM', text: "Understood. Nieramind.AI supports native integrations with Salesforce, HubSpot, and Microsoft Dynamics. I've attached a PDF of our integration roadmap below.", attachment: { name: 'Integration_Roadmap_2023.pdf', size: '1.2 MB' } },
      { bot: false, time: '12:45 PM', text: "Can we schedule a proposal review? I'm interested in the Enterprise plan." },
    ],
    emails: [
      { id: 1, subject: 'Enterprise Q4 Plan \u2014 Proposal Details', from: 'alex@nieramind.ai', to: 'budi.s@example.com', date: 'Sep 13, 2025 \u2014 3:45 PM', body: "Hi Budi,\n\nThank you for your interest in our Enterprise Q4 expansion plan. As discussed, I've attached the full proposal including pricing tiers, feature breakdown, and the integration roadmap.\n\nPlease let me know if you'd like to schedule a walkthrough call.\n\nBest regards,\nAlex Rivera\nNieramind.AI", attachments: ['Q4_Enterprise_Proposal.pdf', 'Integration_Roadmap.pdf'], read: true },
      { id: 2, subject: 'Re: Enterprise Q4 Plan \u2014 Proposal Details', from: 'budi.s@example.com', to: 'alex@nieramind.ai', date: 'Sep 14, 2025 \u2014 9:10 AM', body: "Hi Alex,\n\nThanks for sending over the proposal. The pricing looks competitive. My main concern is around the CRM integration.\n\nCan we schedule a proposal review call this week?\n\nBest,\nBudi Santoso", attachments: [], read: true },
      { id: 3, subject: 'Meeting Confirmation \u2014 Proposal Review', from: 'alex@nieramind.ai', to: 'budi.s@example.com', date: 'Sep 14, 2025 \u2014 1:20 PM', body: "Hi Budi,\n\nGreat news! I've booked a proposal review session for Thursday Sep 21 at 2:00 PM (WIB). You'll receive a calendar invite shortly.\n\nLooking forward to it!\n\nAlex", attachments: ['Meeting_Invite.ics'], read: false },
    ]
  },
  {
    id: 2,
    name: 'Siti Aminah', initials: 'SA',
    avatarGrad: `linear-gradient(135deg, ${C.primary}, ${C.amber})`,
    role: 'Procurement at Global Retail',
    email: 'siti.a@retail.co.id', phone: '+62 811-9988-7766', location: 'Surabaya, Indonesia',
    online: false,
    pills: [['Retail', '#f1eeec', C.t2], ['Lead', C.pL, C.primary]],
    time: 'Yesterday',
    preview: "Thank you for the documentation. I'll review it.",
    tags: [['Discovery', '#94a3b8']],
    status: 'active',
    stages: [
      { label: 'New Inquiry', sub: 'Completed Oct 1', state: 'check' },
      { label: 'Discovery Call', sub: 'Current', state: 'current' },
      { label: 'Proposal Sent', sub: 'Pending', state: 'pending' },
      { label: 'Closing', sub: 'Pending', state: 'pending' },
    ],
    details: { company: 'Global Retail Corp', industry: 'E-commerce', dealValue: '$8,200', source: 'Google', assigned: 'Michael Do', priority: 'Medium', created: 'Oct 01, 2025' },
    dateLabel: 'YESTERDAY',
    messages: [
      { bot: true, time: '10:00 AM', text: "Good morning, Siti! Welcome to Nieramind.AI. I noticed you signed up through our retail solutions page. How can I assist you today?" },
      { bot: false, time: '10:15 AM', text: "Hi, we're evaluating AI chatbot platforms for our customer service portal. Do you support multi-currency?" },
      { bot: true, time: '10:17 AM', text: "Absolutely! Nieramind.AI supports multi-currency including IDR, USD, SGD, and more. Here's our retail solutions brochure.", attachment: { name: 'Retail_Solutions_2025.pdf', size: '3.4 MB' } },
      { bot: false, time: '10:30 AM', text: "Thank you for the detailed information. I'll get back to you after discussing with our procurement team." },
    ],
    emails: []
  },
  {
    id: 3, name: 'Dian Wijaya', initials: 'DW',
    avatarGrad: `linear-gradient(135deg, #6366f1, #a855f7)`,
    role: 'CTO at DataFlow',
    email: 'dian.w@dataflow.id', phone: '+62 813-5566-7788', location: 'Jakarta, Indonesia',
    online: false,
    pills: [['Tech', '#f1eeec', C.t2], ['Closed', '#dcfce7', C.green]],
    time: 'Oct 12',
    preview: 'Issue resolved. Marking as closed.',
    tags: [['Resolved', C.green]],
    status: 'completed',
    stages: [
      { label: 'New Inquiry', sub: 'Completed', state: 'check' },
      { label: 'Discovery Call', sub: 'Completed', state: 'check' },
      { label: 'Proposal Sent', sub: 'Completed', state: 'check' },
      { label: 'Closing', sub: 'Completed', state: 'check' },
    ],
    details: { company: 'DataFlow Inc.', industry: 'Data Analytics', dealValue: '$5,400', source: 'Referral', assigned: 'Sarah Chen', priority: 'Low', created: 'Sep 28, 2025' },
    dateLabel: 'OCT 12',
    messages: [
      { bot: false, time: '09:00 AM', text: "The integration issue we discussed has been resolved on our end." },
      { bot: true, time: '09:01 AM', text: "That's great to hear, Dian! I'll mark this conversation as resolved. Don't hesitate to reach out if you need anything else." },
    ],
    emails: []
  },
  {
    id: 4, name: 'Reza Pratama', initials: 'RP',
    avatarGrad: `linear-gradient(135deg, #10b981, #3b82f6)`,
    role: 'Sales Manager at Indo Trading',
    email: 'reza.p@indotrading.com', phone: '+62 856-4433-2211', location: 'Medan, Indonesia',
    online: true,
    pills: [['Enterprise', '#f1eeec', C.t2], ['Hot Lead', '#fef3c7', '#d97706']],
    time: '2:15 PM',
    preview: 'Sending the contract now. Please review.',
    tags: [['Contract', '#3b82f6']],
    status: 'active',
    stages: [
      { label: 'New Inquiry', sub: 'Completed', state: 'check' },
      { label: 'Discovery Call', sub: 'Completed', state: 'check' },
      { label: 'Proposal Sent', sub: 'Completed', state: 'check' },
      { label: 'Closing', sub: 'In Progress', state: 'current' },
    ],
    details: { company: 'Indo Trading Co.', industry: 'Import/Export', dealValue: '$32,000', source: 'Cold Outreach', assigned: 'Alex Rivera', priority: 'High', created: 'Aug 15, 2025' },
    dateLabel: 'TODAY',
    messages: [
      { bot: true, time: '01:00 PM', text: "Hi Reza! Just following up on the contract. Our legal team has finalized the terms based on our last discussion." },
      { bot: false, time: '02:15 PM', text: "Sending the contract now. Please review and let me know if anything needs adjustment." },
    ],
    emails: []
  },
  {
    id: 5, name: 'Lina Tan', initials: 'LT',
    avatarGrad: `linear-gradient(135deg, #f43f5e, #fb923c)`,
    role: 'Support Lead at Nusantara Tech',
    email: 'lina.t@nusatech.co.id', phone: '+62 877-1234-5678', location: 'Bali, Indonesia',
    online: true,
    pills: [['Support', '#f1eeec', C.t2], ['Escalated', '#fef2f2', C.red]],
    time: '1 hr ago',
    preview: 'I need to speak with a human agent.',
    tags: [['Urgent', C.red], ['Escalation', '#f59e0b']],
    status: 'needs-attention',
    stages: [
      { label: 'New Inquiry', sub: 'Completed', state: 'check' },
      { label: 'Discovery Call', sub: 'Escalated', state: 'current' },
      { label: 'Proposal Sent', sub: 'Pending', state: 'pending' },
      { label: 'Closing', sub: 'Pending', state: 'pending' },
    ],
    details: { company: 'Nusantara Tech', industry: 'IT Services', dealValue: '$6,800', source: 'Support Ticket', assigned: 'Unassigned', priority: 'Urgent', created: 'Oct 14, 2025' },
    dateLabel: 'TODAY',
    messages: [
      { bot: false, time: '3:00 PM', text: "I've been having issues with the API endpoint for the past 3 hours. This is affecting our production environment." },
      { bot: true, time: '3:01 PM', text: "I'm sorry to hear that, Lina. I can see there's been an elevated error rate on your account. Let me escalate this to our engineering team immediately." },
      { bot: false, time: '3:05 PM', text: "I need to speak with a human agent. This is critical and we need a resolution within the hour." },
    ],
    emails: []
  },
];

/* ═══════════════════════════════════════════════════════════
   Main Component
   ═══════════════════════════════════════════════════════════ */
export default function ConversationsPage() {
  const { isMobile, isTablet, isWide } = useBreakpoint();
  const mob = isMobile;

  const [activeFilter, setActiveFilter] = useState('all');
  /* FIX #2: Start with null so mobile shows conversation list first.
     Desktop/tablet always show both panels — we fall back to
     CONVERSATIONS[0] when nothing is explicitly selected yet. */
  const [selectedId, setSelectedId] = useState(null);
  const [activeTab, setActiveTab] = useState('chat');
  const [msgText, setMsgText] = useState('');
  const [liveMessages, setLiveMessages] = useState({});
  const [showProfile, setShowProfile] = useState(false);
  const [openEmailId, setOpenEmailId] = useState(null);

  const chatEndRef = useRef(null);
  const scrollAreaRef = useRef(null);

  /* Resolve active conversation — fallback to first entry on desktop/tablet */
  const activeId = selectedId || CONVERSATIONS[0]?.id;
  const selected = CONVERSATIONS.find(c => c.id === activeId) || CONVERSATIONS[0];

  /* Merge seed + live messages */
  const allMsgs = [...(selected.messages || []), ...(liveMessages[activeId] || [])];

  /* ── Send message with AI auto-reply ── */
  const handleSend = () => {
    if (!msgText.trim()) return;
    const now = new Date();
    const t = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    const userMsg = { bot: false, time: t, text: msgText.trim() };
    setLiveMessages(prev => ({ ...prev, [activeId]: [...(prev[activeId] || []), userMsg] }));
    setMsgText('');

    /* AI auto-reply after 1.2s */
    setTimeout(() => {
      const rt = new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
      const replies = [
        "Thank you for your message! Let me look into that for you right away.",
        "Got it! I'll check our system and get back to you with the details.",
        "Understood. I'm pulling up the relevant information now.",
        "Thanks for the update! I'll make sure our team is aligned on this.",
        "I appreciate you sharing that. Let me prepare a detailed response.",
      ];
      const botMsg = { bot: true, time: rt, text: replies[Math.floor(Math.random() * replies.length)] };
      setLiveMessages(prev => ({ ...prev, [activeId]: [...(prev[activeId] || []), botMsg] }));
    }, 1200);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
    if (e.key === 'Escape') setMsgText('');
  };

  /* FIX #4: Scroll only the chat scroll container, not the whole page */
  useEffect(() => {
    if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
    }
  }, [allMsgs.length, activeId]);

  /* Selecting a conversation resets tabs */
  const selectConvo = (id) => { setSelectedId(id); setActiveTab('chat'); setOpenEmailId(null); };

  /* Go back to list (mobile) */
  const goBackToList = () => { setSelectedId(null); };

  const filtered = CONVERSATIONS.filter(c => activeFilter === 'all' || c.status === activeFilter);

  /* Mobile single-panel logic:
     Mobile  → list when no selection, chat when selected
     Desktop → both panels always visible */
  const showList = !isMobile || !selectedId;
  const showChat = !isMobile || !!selectedId;

  /* Unread email count */
  const unreadEmailCount = (selected.emails || []).filter(e => !e.read).length;

  /* ══════ Sub-Component: Profile Content ══════ */
  const ProfileContent = () => (
    <div>
      {/* Avatar + Info */}
      <div style={{ textAlign: 'center', padding: '32px 24px', borderBottom: `1px solid ${C.bdr}` }}>
        <div style={{ position: 'relative', display: 'inline-block', marginBottom: 16 }}>
          <div style={{ width: 80, height: 80, borderRadius: '50%', background: selected.avatarGrad, display: 'grid', placeItems: 'center', color: '#fff', fontSize: 28, fontWeight: 800 }}>
            {selected.initials}
          </div>
          {selected.online && (
            <div style={{ position: 'absolute', bottom: 2, right: 2, width: 14, height: 14, borderRadius: '50%', background: C.green, border: `2.5px solid ${C.w}` }} />
          )}
        </div>
        <h3 style={{ fontSize: 17, fontWeight: 800, color: C.t1, marginBottom: 4 }}>{selected.name}</h3>
        <p style={{ fontSize: 13, color: C.t2, marginBottom: 12 }}>{selected.role}</p>
        {selected.pills && (
          <div style={{ display: 'flex', gap: 6, justifyContent: 'center', flexWrap: 'wrap' }}>
            {selected.pills.map(([label, bg, color], i) => (
              <Pill key={i} bg={bg} color={color}>{label}</Pill>
            ))}
          </div>
        )}
      </div>

      {/* Contact Info */}
      <div style={{ padding: 24, borderBottom: `1px solid ${C.bdr}` }}>
        <h4 style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.1em', color: C.t3, marginBottom: 16 }}>Contact Info</h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, fontSize: 13, color: C.t2 }}>
          {selected.email && <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}><Icon name="mail" size={18} style={{ color: C.t3, flexShrink: 0 }} /> <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selected.email}</span></div>}
          {selected.phone && <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}><Icon name="call" size={18} style={{ color: C.t3, flexShrink: 0 }} /> {selected.phone}</div>}
          {selected.location && <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}><Icon name="location_on" size={18} style={{ color: C.t3, flexShrink: 0 }} /> {selected.location}</div>}
        </div>
      </div>

      {/* Conversion Journey */}
      {selected.stages && (
        <div style={{ padding: 24, borderBottom: `1px solid ${C.bdr}` }}>
          <h4 style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.1em', color: C.t3, marginBottom: 16 }}>Conversion Journey</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {selected.stages.map((s, i) => {
              const isCheck = s.state === 'check';
              const isCurrent = s.state === 'current';
              const dotColor = isCheck ? C.green : isCurrent ? C.primary : C.bdr;
              const isLast = i === selected.stages.length - 1;
              return (
                <div key={i} style={{ display: 'flex', gap: 14, position: 'relative', paddingBottom: isLast ? 0 : 20 }}>
                  {/* Vertical line */}
                  {!isLast && <div style={{ position: 'absolute', left: 11, top: 24, bottom: 0, width: 2, background: isCheck ? C.green : C.bdr }} />}
                  {/* Dot */}
                  <div style={{ width: 24, height: 24, borderRadius: '50%', background: dotColor, display: 'grid', placeItems: 'center', flexShrink: 0, zIndex: 1 }}>
                    {isCheck && <Icon name="check" size={14} style={{ color: '#fff' }} />}
                    {isCurrent && <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff' }} />}
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.t1 }}>{s.label}</div>
                    <div style={{ fontSize: 11, color: C.t3 }}>{s.sub}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div style={{ padding: 24 }}>
        <h4 style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.1em', color: C.t3, marginBottom: 16 }}>Quick Actions</h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[['calendar_month', 'Book Meeting'], ['receipt_long', 'Generate Invoice'], ['check_circle', 'Mark as Resolved']].map(([ic, lbl]) => (
            <button key={lbl} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 10, border: `1px solid ${C.bdr}`, background: C.w, fontSize: 12, fontWeight: 600, color: C.t2, cursor: 'pointer', transition: 'all .15s', width: '100%', textAlign: 'left' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = C.primary; e.currentTarget.style.color = C.primary; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = C.bdr; e.currentTarget.style.color = C.t2; }}>
              <Icon name={ic} size={18} /> {lbl}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  /* ═══════════════════════════════════════════════════════════
     RENDER
     ═══════════════════════════════════════════════════════════ */
  return (
    <div style={{ display: 'flex', height: '100%', background: C.bg, overflow: 'hidden' }}>

      {/* ══════════════════════════════════════
          LEFT LIST
         ══════════════════════════════════════ */}
      {showList && (
        <div style={{
          width: isMobile ? '100%' : isTablet ? 280 : 350,
          flexShrink: 0,
          borderRight: isMobile ? 'none' : `1px solid ${C.bdr}`,
          background: C.w, display: 'flex', flexDirection: 'column',
        }}>
          {/* Header + Filters */}
          <div style={{ padding: mob ? '16px 16px 12px' : '20px 20px 12px', borderBottom: `1px solid ${C.bdr}`, flexShrink: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h2 style={{ fontSize: 18, fontWeight: 800, margin: 0 }}>Messages</h2>
              <button style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${C.bdr}`, background: C.w, display: 'grid', placeItems: 'center', cursor: 'pointer', color: C.t2 }}>
                <Icon name="edit_square" size={18} />
              </button>
            </div>
            {/* Filter row — horizontal scroll prevents overflow on tablet (280px panel) */}
            <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4, WebkitOverflowScrolling: 'touch' }}>
              {['all', 'active', 'completed', 'needs-attention'].map(f => {
                const labels = { all: 'All', active: 'Active', completed: 'Completed', 'needs-attention': 'Needs attention' };
                const count = CONVERSATIONS.filter(c => f === 'all' || c.status === f).length;
                return (
                  <button key={f} onClick={() => setActiveFilter(f)} style={{
                    padding: '5px 10px', borderRadius: 8, border: 'none', cursor: 'pointer',
                    whiteSpace: 'nowrap', fontSize: 11, fontWeight: 700, transition: 'all .15s',
                    background: activeFilter === f ? C.pL : 'transparent',
                    color: activeFilter === f ? C.primary : C.t2,
                    flexShrink: 0,
                  }}>
                    {labels[f]} <span style={{ opacity: 0.6, marginLeft: 2 }}>{count}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Conversation list */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {filtered.map(c => (
              <div key={c.id} onClick={() => selectConvo(c.id)}
                style={{
                  padding: mob ? '14px 16px' : '16px 20px', cursor: 'pointer', transition: 'background .1s',
                  background: activeId === c.id ? '#fafafa' : 'transparent',
                  borderLeft: `4px solid ${activeId === c.id ? C.primary : 'transparent'}`,
                  borderBottom: `1px solid ${C.bdr}`,
                }}>
                <div style={{ display: 'flex', gap: 12 }}>
                  <div style={{ position: 'relative', flexShrink: 0 }}>
                    <div style={{ width: 40, height: 40, borderRadius: 10, background: c.avatarGrad, display: 'grid', placeItems: 'center', color: '#fff', fontWeight: 700, fontSize: 14 }}>{c.initials}</div>
                    {c.online && <div style={{ position: 'absolute', bottom: -1, right: -1, width: 10, height: 10, borderRadius: '50%', background: C.green, border: `1.5px solid ${C.w}` }} />}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                      <span style={{ fontWeight: 700, fontSize: 13 }}>{c.name}</span>
                      <span style={{ fontSize: 10, color: C.t3, flexShrink: 0 }}>{c.time}</span>
                    </div>
                    <p style={{ fontSize: 12, color: C.t2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', margin: '0 0 6px' }}>{c.preview}</p>
                    {c.tags && (
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {c.tags.map(([t, col]) => (
                          <span key={t} style={{ fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: `${col}18`, color: col, textTransform: 'uppercase' }}>{t}</span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════
          CENTER — Chat / Details / Email
         ══════════════════════════════════════ */}
      {showChat && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: C.w, minWidth: 0 }}>

          {/* ── Chat Header: back (mobile) + avatar + name/status + profile btn ── */}
          <div style={{
            padding: mob ? '10px 14px' : '10px 20px',
            borderBottom: `1px solid ${C.bdr}`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            minHeight: 56, flexShrink: 0, background: C.w,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
              {isMobile && (
                <button onClick={goBackToList} aria-label="Back to conversations"
                  style={{ border: 'none', background: 'none', cursor: 'pointer', color: C.t1, display: 'grid', placeItems: 'center', padding: 4, flexShrink: 0 }}>
                  <Icon name="arrow_back" size={22} />
                </button>
              )}
              <div style={{ position: 'relative', flexShrink: 0 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: selected.avatarGrad, display: 'grid', placeItems: 'center', color: '#fff', fontWeight: 700, fontSize: 13 }}>{selected.initials}</div>
                {selected.online && <div style={{ position: 'absolute', bottom: -1, right: -1, width: 9, height: 9, borderRadius: '50%', background: C.green, border: `1.5px solid ${C.w}` }} />}
              </div>
              <div style={{ minWidth: 0 }}>
                <h3 style={{ fontSize: 15, fontWeight: 800, margin: 0, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selected.name}</h3>
                <span style={{ fontSize: 11, color: selected.online ? C.green : C.t3, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: selected.online ? C.green : C.t3, display: 'inline-block', flexShrink: 0 }} />
                  {selected.online ? 'Online' : 'Offline'}
                </span>
              </div>
            </div>

            {/* Profile button — tablet/mobile only (desktop shows inline panel) */}
            {!isWide && (
              <button onClick={() => setShowProfile(true)} aria-label="View profile"
                style={{ background: C.pL, border: 'none', width: 36, height: 36, borderRadius: 10, display: 'grid', placeItems: 'center', color: C.primary, cursor: 'pointer', flexShrink: 0 }}>
                <Icon name="person" size={20} />
              </button>
            )}
          </div>

          {/* ── Tabs ──
              FIX #3: Icon names corrected for Material Symbols Outlined:
              chat_bubble_outline → chat_bubble
              info_outline        → info
              mail_outline        → mail                                */}
          <div style={{ display: 'flex', borderBottom: `1px solid ${C.bdr}`, padding: mob ? '0 14px' : '0 20px', gap: mob ? 0 : 24, flexShrink: 0, background: C.w }}>
            {[
              { key: 'chat', label: 'Conversation', icon: 'chat_bubble' },
              { key: 'details', label: 'Details', icon: 'info' },
              { key: 'email', label: 'Email History', icon: 'mail' },
            ].map(t => {
              const isSel = activeTab === t.key;
              return (
                <button key={t.key} onClick={() => { setActiveTab(t.key); setOpenEmailId(null); }} style={{
                  padding: mob ? '12px 14px' : '14px 0',
                  border: 'none', background: 'none',
                  borderBottom: `2px solid ${isSel ? C.primary : 'transparent'}`,
                  color: isSel ? C.primary : C.t3,
                  fontSize: 13, fontWeight: 700, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 6, transition: 'color .12s',
                  whiteSpace: 'nowrap',
                }}>
                  {mob && <Icon name={t.icon} size={18} />}
                  {!mob && <Icon name={t.icon} size={16} />}
                  {!mob && t.label}
                  {t.key === 'email' && unreadEmailCount > 0 && (
                    <span style={{ fontSize: 9, fontWeight: 800, background: C.primary, color: '#fff', borderRadius: 99, minWidth: 16, height: 16, display: 'grid', placeItems: 'center', padding: '0 4px' }}>{unreadEmailCount}</span>
                  )}
                </button>
              );
            })}
          </div>

          {/* ══════ CHAT TAB ══════ */}
          {activeTab === 'chat' && (
            <>
              {/* Messages — scrollable container with ref for auto-scroll */}
              <div ref={scrollAreaRef} style={{ flex: 1, overflowY: 'auto', padding: mob ? 16 : 24, display: 'flex', flexDirection: 'column', gap: 20, background: C.bg }}>
                {/* Date label */}
                {selected.dateLabel && (
                  <div style={{ textAlign: 'center' }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: C.t3, textTransform: 'uppercase', letterSpacing: '.1em', background: '#f1eeec', padding: '4px 14px', borderRadius: 99 }}>{selected.dateLabel}</span>
                  </div>
                )}
                {allMsgs.map((m, i) => (
                  <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: m.bot ? 'flex-start' : 'flex-end', maxWidth: mob ? '90%' : '75%', marginLeft: m.bot ? 0 : 'auto' }}>
                    {/* Sender label + time */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, flexDirection: m.bot ? 'row' : 'row-reverse' }}>
                      {m.bot && <div style={{ width: 22, height: 22, borderRadius: '50%', background: C.teal, display: 'grid', placeItems: 'center' }}><Icon name="smart_toy" size={13} style={{ color: '#fff' }} /></div>}
                      {m.bot && <span style={{ fontSize: 11, fontWeight: 700, color: C.teal }}>Nieramind AI</span>}
                      <span style={{ fontSize: 10, color: C.t3 }}>{m.time}{!m.bot && ' \u2022 Sent'}</span>
                    </div>
                    {/* Bubble */}
                    <div style={{
                      padding: '12px 16px', fontSize: mob ? 14 : 13, lineHeight: 1.6,
                      borderRadius: 14,
                      borderTopLeftRadius: m.bot ? 4 : 14,
                      borderTopRightRadius: m.bot ? 14 : 4,
                      background: m.bot ? C.w : C.primary,
                      color: m.bot ? C.t1 : '#fff',
                      boxShadow: '0 1px 3px rgba(0,0,0,.06)',
                    }}>{m.text}</div>
                    {/* Attachment */}
                    {m.attachment && (
                      <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: C.w, border: `1px solid ${C.bdr}`, borderRadius: 10, maxWidth: 280 }}>
                        <Icon name="picture_as_pdf" size={22} style={{ color: C.red }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.attachment.name}</div>
                          <div style={{ fontSize: 10, color: C.t3 }}>{m.attachment.size}</div>
                        </div>
                        <Icon name="download" size={20} style={{ color: C.t3, cursor: 'pointer' }} />
                      </div>
                    )}
                  </div>
                ))}
                <div ref={chatEndRef} />
              </div>

              {/* ── Beautified Chat Input ── */}
              <div style={{
                padding: mob ? '10px 12px' : '12px 16px',
                paddingBottom: mob ? 'max(12px, env(safe-area-inset-bottom, 12px))' : 14,
                background: C.w, borderTop: `1px solid ${C.bdr}`, flexShrink: 0,
              }}>
                <div style={{ border: `1px solid ${C.bdr}`, borderRadius: 14, overflow: 'hidden' }}>
                  <textarea
                    value={msgText}
                    onChange={e => setMsgText(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Type a message or use '/' for commands..."
                    style={{ width: '100%', border: 'none', outline: 'none', resize: 'none', padding: mob ? '10px 14px' : '14px 16px', height: mob ? 48 : 64, fontSize: 13, background: '#faf9f8', fontFamily: 'inherit', boxSizing: 'border-box' }}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: mob ? '6px 10px' : '8px 14px', background: '#f1eeec' }}>
                    <div style={{ display: 'flex', gap: mob ? 4 : 6 }}>
                      {['attach_file', 'mood', 'image'].map(ic => (
                        <button key={ic} style={{ border: 'none', background: 'none', cursor: 'pointer', color: C.t2, display: 'grid', placeItems: 'center', padding: 4, borderRadius: 6 }}>
                          <Icon name={ic} size={mob ? 18 : 20} />
                        </button>
                      ))}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      {!mob && <span style={{ fontSize: 10, color: C.t3 }}>Esc to cancel &bull; Enter to send</span>}
                      <button onClick={handleSend} style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        padding: mob ? '7px 14px' : '8px 16px', borderRadius: 8, border: 'none',
                        background: msgText.trim() ? C.primary : '#ccc',
                        color: '#fff', fontSize: 12, fontWeight: 700,
                        cursor: msgText.trim() ? 'pointer' : 'default',
                        transition: 'background .15s',
                      }}>
                        <Icon name="send" size={16} /> Send
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* ══════ DETAILS TAB ══════ */}
          {activeTab === 'details' && selected.details && (
            <div style={{ flex: 1, overflowY: 'auto', padding: mob ? 16 : 24 }}>
              <div style={{ display: 'grid', gridTemplateColumns: mob ? '1fr' : '1fr 1fr', gap: 16 }}>
                {Object.entries(selected.details).map(([key, val]) => {
                  const labels = { company: 'Company', industry: 'Industry', dealValue: 'Deal Value', source: 'Source', assigned: 'Assigned To', priority: 'Priority', created: 'Created' };
                  return (
                    <div key={key} style={{ padding: 16, background: C.bg, borderRadius: 12 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: C.t3, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>{labels[key] || key}</div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: (key === 'priority' && (val === 'High' || val === 'Urgent')) ? C.red : key === 'dealValue' ? C.teal : C.t1 }}>{val}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ══════ EMAIL TAB ══════ */}
          {activeTab === 'email' && (
            <div style={{ flex: 1, overflowY: 'auto', padding: mob ? 16 : 24, display: 'flex', flexDirection: 'column', gap: 12 }}>
              {(selected.emails || []).length === 0 ? (
                <div style={{ textAlign: 'center', padding: 48, color: C.t3 }}>
                  <Icon name="mail" size={36} style={{ display: 'block', margin: '0 auto 10px', color: C.t3 }} />
                  <div style={{ fontSize: 14, fontWeight: 600 }}>No emails yet</div>
                  <div style={{ fontSize: 12 }}>Email history will appear here</div>
                </div>
              ) : selected.emails.map(em => {
                const isOpen = openEmailId === em.id;
                return (
                  <div key={em.id} style={{ border: `1px solid ${C.bdr}`, borderRadius: 12, overflow: 'hidden', background: C.w }}>
                    {/* Email header row */}
                    <button onClick={() => setOpenEmailId(isOpen ? null : em.id)} style={{
                      width: '100%', padding: '14px 16px', border: 'none', background: 'none', cursor: 'pointer', textAlign: 'left',
                      display: 'flex', alignItems: 'center', gap: 12,
                      borderLeft: em.read ? 'none' : `3px solid ${C.primary}`,
                    }}>
                      <Icon name={isOpen ? 'drafts' : 'mail'} size={20} style={{ color: em.read ? C.t3 : C.primary, flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: em.read ? 500 : 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{em.subject}</div>
                        <div style={{ fontSize: 11, color: C.t3, marginTop: 2 }}>{em.from} &bull; {em.date}</div>
                      </div>
                      <Icon name={isOpen ? 'expand_less' : 'expand_more'} size={20} style={{ color: C.t3, flexShrink: 0 }} />
                    </button>
                    {/* Expanded body */}
                    {isOpen && (
                      <div style={{ padding: '0 16px 16px', borderTop: `1px solid ${C.bdr}` }}>
                        <div style={{ display: 'flex', gap: 8, padding: '10px 0', fontSize: 11, color: C.t3, flexWrap: 'wrap' }}>
                          <span><b>From:</b> {em.from}</span>
                          <span><b>To:</b> {em.to}</span>
                        </div>
                        <pre style={{ fontSize: 13, color: C.t2, lineHeight: 1.7, whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: 0 }}>{em.body}</pre>
                        {em.attachments && em.attachments.length > 0 && (
                          <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                            {em.attachments.map((a, i) => (
                              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 8, background: C.bg, border: `1px solid ${C.bdr}`, fontSize: 11, fontWeight: 600 }}>
                                <Icon name={a.endsWith('.pdf') ? 'picture_as_pdf' : a.endsWith('.ics') ? 'event' : 'attach_file'} size={14} style={{ color: a.endsWith('.pdf') ? C.red : C.t3 }} />
                                {a}
                              </div>
                            ))}
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                          <Btn icon="reply">Reply</Btn>
                          <Btn icon="forward">Forward</Btn>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════
          RIGHT PROFILE (Desktop >=1200px only)
         ══════════════════════════════════════ */}
      {isWide && (
        <div style={{ width: 300, flexShrink: 0, borderLeft: `1px solid ${C.bdr}`, background: C.w, overflowY: 'auto' }}>
          <ProfileContent />
        </div>
      )}

      {/* ══════════════════════════════════════
          MOBILE/TABLET SLIDE-OVER PROFILE
         ══════════════════════════════════════ */}
      {!isWide && showProfile && (
        <>
          <div onClick={() => setShowProfile(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.3)', zIndex: 1055 }} />
          <div style={{
            position: 'fixed', top: 0, right: 0, bottom: 0,
            width: isMobile ? '85%' : 340, maxWidth: 380,
            zIndex: 1060, background: C.w, overflowY: 'auto',
            boxShadow: '-8px 0 30px rgba(0,0,0,.1)',
          }}>
            <div style={{ padding: '12px 16px', display: 'flex', justifyContent: 'flex-end', borderBottom: `1px solid ${C.bdr}` }}>
              <button onClick={() => setShowProfile(false)} aria-label="Close profile"
                style={{ border: 'none', background: '#f1eeec', borderRadius: 8, width: 32, height: 32, cursor: 'pointer', display: 'grid', placeItems: 'center', color: C.t2 }}>
                <Icon name="close" size={18} />
              </button>
            </div>
            <ProfileContent />
          </div>
        </>
      )}
    </div>
  );
}
